import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { useCreateCategoryMutation } from '../../app/api';

const CategoryAdd = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [createCategory, { isLoading: loading }] = useCreateCategoryMutation();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    status: 'Active',
    image: null,
    imagePreview: null,
  });
  const [errors, setErrors] = useState({});

  const generateSlug = (name) =>
    name.toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'name') newData.slug = generateSlug(value);
      return newData;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, image: 'Only JPEG, PNG, WEBP allowed' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: 'Max 5MB allowed' }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, image: file, imagePreview: reader.result }));
      setErrors((prev) => ({ ...prev, image: '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null, imagePreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Category name is required';
    if (!formData.slug.trim()) e.slug = 'Slug is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('slug', formData.slug);
      fd.append('description', formData.description);
      fd.append('status', formData.status);
      if (formData.image) fd.append('image', formData.image);

      await createCategory(fd).unwrap();
      toast.success('Category created successfully!');
      navigate('/categories');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create category');
    }
  };

  return (
    <Layout>
      <div className="add-category-page">
        <div className="page-header">
          <div>
            <Link
              to="/categories"
              className="btn btn-sm btn-outline btn-back"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}
            >
              <ArrowLeft size={16} />
              <span>Back to Categories</span>
            </Link>
            <h1 className="page-title">Add New Category</h1>
            <p className="page-subtitle">Create a new product category</p>
          </div>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Category Image</label>
              <div className="image-upload-container">
                {formData.imagePreview ? (
                  <div className="image-preview-wrapper">
                    <img src={formData.imagePreview} alt="Preview" className="image-preview" />
                    <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="image-upload-area" onClick={() => fileInputRef.current?.click()}>
                    <ImageIcon size={40} className="upload-icon" />
                    <p className="upload-text">Click to upload image</p>
                    <span className="upload-hint">PNG, JPG, WEBP (Max 5MB)</span>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                {errors.image && <div className="form-error">{errors.image}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? 'error' : ''}`}
                  placeholder="e.g. Herbal Teas"
                />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Slug <span className="required">*</span></label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  readOnly
                  className={`form-control ${errors.slug ? 'error' : ''}`}
                  style={{ background: 'var(--color-bg-primary)', cursor: 'not-allowed' }}
                />
                {errors.slug && <div className="form-error">{errors.slug}</div>}
                <div className="form-text">Auto-generated from name</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-control">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-actions">
              <Link to="/categories" className="btn btn-outline">Cancel</Link>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Category'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CategoryAdd;